import { defineNuxtPlugin, useRouter } from 'nuxt/app'
import { watch } from 'vue'
import { Pinia } from 'pinia'
import { isSearchUrlRoute } from '~/apps/search/isSearchUrlComponent'
import { useSearchStore } from '../stores/useSearchStore'
import { logger } from '../utility/logger'
import { generateUUID } from '../utility/uuid'

export default defineNuxtPlugin(() => {
  logger('search-history.client')
  const router = useRouter()
  const pinia = usePinia() as Pinia
  const authStore = useAuthStore(pinia)
  const searchStore = useSearchStore(pinia)
  let language = 'en-gb'
  router.afterEach(async (to) => {
    language = (to.params.lang as string) || 'en-gb'
  })
  const createUrl = '/_/fallback/search2/history/'
  const searchHistoryTokenKey = 'searchHistoryToken'

  if (process.client) {
    watch(
      () => authStore.isLoggedIn,
      async (newValue) => {
        if (newValue) {
          await syncSearchHistory()
        }
      },
    )
  }

  searchStore.service.onTransition(async (state) => {
    if (
      state.matches('results') &&
      isSearchUrlRoute(
        { path: searchStore.recentSearchUrl },
        [],
        ['searchedit', 'searcheditmaps'],
        ['near-me'],
      )
    ) {
      await authStore.authCheck
      let searchHistoryToken = localStorage.getItem(searchHistoryTokenKey)
      if (!authStore.isLoggedIn && !searchHistoryToken) {
        searchHistoryToken = generateUUID()
        localStorage.setItem(searchHistoryTokenKey, searchHistoryToken)
      }
      if (searchStore.context.results) {
        const data = JSON.stringify({
          url: searchStore.recentSearchUrl,
          filters: searchStore.context.filters,
          language: language,
          search_context: searchStore.context.results
            ? {
                ...(searchStore.context.results.heading && {
                  heading: searchStore.context.results.heading,
                }),
                ...(searchStore.context.results.hierarchyName && {
                  hierarchyName: searchStore.context.results.hierarchyName,
                }),
              }
            : null,
          token: searchHistoryToken,
        })

        if (process.client && navigator.sendBeacon) {
          const blob = new Blob([data], { type: 'application/json' })
          const sent = navigator.sendBeacon(createUrl, blob)
          if (!sent) {
            await sendViaFetch(createUrl, data)
          }
        } else {
          await sendViaFetch(createUrl, data)
        }
      }
    }
  })

  async function sendViaFetch(url: string, data: string) {
    await $fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data,
    }).catch(() => {})
  }

  async function syncSearchHistory() {
    const searchHistoryToken = localStorage.getItem(searchHistoryTokenKey)
    if (!searchHistoryToken) return
    await sendViaFetch(
      '/_/fallback/search2/history/sync/',
      JSON.stringify({ token: searchHistoryToken }),
    )
    localStorage.removeItem(searchHistoryTokenKey)
  }
})
